/**
 * FTR4: procedural instanced asteroid belt between Mars and Jupiter.
 * Seeded (deterministic) so it is stable across reloads. Lives under the
 * worldGroup; like bodies, it is placed camera-relative each frame by feeding
 * the ship's Float64 position as a uniform offset to the instance transforms.
 *
 * Real belt: a ~ 2.2-3.3 AU. With distanceCompression 0.1 and 1u=1km that is
 * ~22.8M-49.4M units from Sol. We seed instances in that annulus.
 *
 * FTR7 perf: per-rock rotation + scale are fixed and cached at construction;
 * `update()` only re-bases position and allocates ZERO objects per frame.
 * Single InstancedMesh => single draw call for the whole belt.
 */
import * as THREE from 'three';
import type { Vec3 } from '@blackmarble/sim-core';

/** Deterministic LCG so the belt is identical every run. */
const makeRng = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
};

/** Shared rock look — single source of truth for the belt + viewer sample. */
const rockGeometry = (): THREE.IcosahedronGeometry => new THREE.IcosahedronGeometry(1, 0);
const rockMaterial = (): THREE.MeshStandardMaterial =>
  new THREE.MeshStandardMaterial({ color: 0x6b6256, roughness: 0.95, metalness: 0 });

/**
 * VIEWER: a COMPACT seeded cluster of belt rocks (same geometry + material as
 * the in-game belt) sized to fit a viewer grid cell. Returns a static
 * InstancedMesh centred at the origin — no floating-origin update needed.
 * @param fitRadius rough radius the cluster should occupy (display units)
 */
export const buildBeltSample = (
  count = 180,
  fitRadius = 1,
  seed = 0xbe17,
): THREE.InstancedMesh => {
  const rng = makeRng(seed);
  const mesh = new THREE.InstancedMesh(rockGeometry(), rockMaterial(), count);
  mesh.frustumCulled = false;
  const dummy = new THREE.Object3D();
  for (let i = 0; i < count; i++) {
    // Thin annulus/disc cluster so it reads as a belt slice.
    const ang = rng() * Math.PI * 2;
    const r = fitRadius * (0.45 + rng() * 0.55);
    const y = (rng() - 0.5) * fitRadius * 0.12;
    dummy.position.set(Math.cos(ang) * r, y, Math.sin(ang) * r);
    dummy.rotation.set(rng() * 6, rng() * 6, rng() * 6);
    dummy.scale.setScalar(fitRadius * (0.03 + rng() * 0.05)); // small rocks
    dummy.updateMatrix();
    mesh.setMatrixAt(i, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  return mesh;
};

export class AsteroidBelt {
  readonly mesh: THREE.InstancedMesh;
  private readonly basePos: Float64Array; // Float64 world positions of each instance
  private readonly quats: Float32Array; // cached per-rock quaternion (x,y,z,w)
  private readonly scales: Float32Array; // cached per-rock uniform scale
  private readonly count: number;
  // Reused scratch — allocated ONCE, never per frame.
  private readonly mtx = new THREE.Matrix4();
  private readonly pos = new THREE.Vector3();
  private readonly quat = new THREE.Quaternion();
  private readonly scl = new THREE.Vector3();
  private readonly euler = new THREE.Euler();

  constructor(
    parent: THREE.Group,
    opts: { count?: number; innerKm?: number; outerKm?: number; seed?: number } = {},
  ) {
    const count = opts.count ?? 4000;
    const inner = opts.innerKm ?? 22_800_000;
    const outer = opts.outerKm ?? 49_400_000;
    const rng = makeRng(opts.seed ?? 0xbe17);
    this.count = count;
    this.basePos = new Float64Array(count * 3);
    this.quats = new Float32Array(count * 4);
    this.scales = new Float32Array(count);

    this.mesh = new THREE.InstancedMesh(rockGeometry(), rockMaterial(), count);
    this.mesh.frustumCulled = false;

    for (let i = 0; i < count; i++) {
      const ang = rng() * Math.PI * 2;
      const r = inner + rng() * (outer - inner);
      const y = (rng() - 0.5) * (outer - inner) * 0.04; // thin disc
      this.basePos[i * 3] = Math.cos(ang) * r;
      this.basePos[i * 3 + 1] = y;
      this.basePos[i * 3 + 2] = Math.sin(ang) * r;
      this.scales[i] = 50 + rng() * 400; // 50-450 km rocks: visible, not collision-real
      this.euler.set(rng() * 6, rng() * 6, rng() * 6);
      this.quat.setFromEuler(this.euler);
      this.quats[i * 4] = this.quat.x;
      this.quats[i * 4 + 1] = this.quat.y;
      this.quats[i * 4 + 2] = this.quat.z;
      this.quats[i * 4 + 3] = this.quat.w;
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    parent.add(this.mesh);
  }

  /** Re-base instances to be camera-relative (floating origin). Zero-alloc. */
  update(shipWorldPos: Vec3): void {
    for (let i = 0; i < this.count; i++) {
      const i3 = i * 3;
      const i4 = i * 4;
      this.pos.set(
        (this.basePos[i3] ?? 0) - shipWorldPos.x,
        (this.basePos[i3 + 1] ?? 0) - shipWorldPos.y,
        (this.basePos[i3 + 2] ?? 0) - shipWorldPos.z,
      );
      this.quat.set(
        this.quats[i4] ?? 0,
        this.quats[i4 + 1] ?? 0,
        this.quats[i4 + 2] ?? 0,
        this.quats[i4 + 3] ?? 1,
      );
      this.scl.setScalar(this.scales[i] ?? 1);
      this.mtx.compose(this.pos, this.quat, this.scl);
      this.mesh.setMatrixAt(i, this.mtx);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
